import { SearchOutlined } from "@ant-design/icons";
import IntlMessages from "@util/IntlMessages";
import { Form, Table } from "antd";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
   addGridFormRef,
   removeGridFormRef,
   setListTablePromise,
   resolveListTablePromise,
} from "../../../../appRedux/actions/FormsV2";
import FormProvider, { useFormContext } from "../../../../context/FormContext/FormContext";
import { deleteListRows, getColumnSum, getList, getListV2 } from "../../../../services/api/Api";
import { saveFormV2 } from "../../../../services/restAPI/RestApi";
import {
   getAllFieldsWithAlerts,
   handleTemplateAlert,
   isActivePublicUser,
} from "../../../../util/Scripts";
import { Button, Checkbox, Input, Message, Modal, Spin } from "../../../BP2Components";
import { Icon_Plus2 } from "../../../BP2Components/Icons/icons";
import Pagination from "../../../BP2Components/Pagination";
import FormComplete from "../../FormComplete";
import {
   handleAllAlerts,
   handleChangedRelatedField,
   handleGridTechname,
   renderDevInfo,
   transformFormValuesForRequest,
} from "../../GeneralScripts";
import GridElementRenderer from "../TableField/Grid/GridElementRenderer";
import ListSum from "./List/ListSum";
import "./ListTableField.less";
import { roundNumbersInListTable } from "./ListTableScripts";
import { SafeCellContent } from "../../../../util/SafeCellContent";
import { useMessageItem } from "../../../../context/MessageItemContext/MessageItemContext";
import FieldInfo from "../FieldInfo/FieldInfo";
import { googleNav } from "../../../../util/actions/googleNav";
import { handleProcessAction } from "../../../../util/actions/process";
import Dropdown from "../../../BP2Components/Dropdown/Dropdown";

const RowActionsDropdownTrigger = ({ actions, buttonLabel }) => {
   const { pendingRequests } = useFormContext();
   const [isOpen, setIsOpen] = React.useState(false);
   const triggerRef = React.useRef(null);

   const items = useMemo(() => {
      return (actions || []).map((action) => ({
         id: action.key,
         name: action.label,
         disabled: !!action.disabled || pendingRequests.current > 0,
         onClick: () => {
            setIsOpen(false);
            action.onClick();
         },
         buttonType: "button",
      }));
   }, [actions, pendingRequests.current > 0]);

   return (
      <div>
         <Button ref={triggerRef} buttonType="primary" onClick={() => setIsOpen(!isOpen)}>
            {buttonLabel}
         </Button>

         <Dropdown
            items={items}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            ignoreRef={triggerRef}
            customZIndex={1000}
            checkForModalRoot={false}
         />
      </div>
   );
};

const EditableRow = ({
   index,
   form,
   record,
   children,
   mainRecordId,
   formRefKey,
   displayFormChildId,
   onRowBlurSnapshot,
   ...trProps
}) => {
   if (record?.editable !== true) {
      return <tr {...trProps}>{children}</tr>;
   }

   const dispatch = useDispatch();
   useEffect(() => {
      dispatch(addGridFormRef({ mainRecordId: formRefKey, gridFormRef: form }));
      return () => dispatch(removeGridFormRef(formRefKey));
   }, [formRefKey]);

   return (
      <FormProvider formId={displayFormChildId} form={form} dataId={mainRecordId}>
         <tr {...trProps} onBlurCapture={onRowBlurSnapshot}>
            {children}
         </tr>
      </FormProvider>
   );
};

const EditableFormRow = Form.create({ name: "form" })(EditableRow);

const EditableCell = ({
   index,
   nestedFormContext,
   techName,
   dataIndex,
   record,
   children,
   ...restProps
}) => {
   const rawValue = record?.[dataIndex];
   if (record?.editable !== true) {
      if (rawValue !== undefined && rawValue !== null) {
         return (
            <td {...restProps}>
               <SafeCellContent content={rawValue} />
            </td>
         );
      }
      if (Array.isArray(children) || React.isValidElement(children)) {
         return <td {...restProps}>{children}</td>;
      }
      return (
         <td {...restProps}>
            <SafeCellContent content={children} />
         </td>
      );
   }

   const { formId, form, formData: nestedFormData, fieldsInfo } = useFormContext();

   const elements =
      nestedFormData?.forms_data?.tab?.reduce((acc, tab) => {
         return {
            ...acc,
            ...tab.elements,
         };
      }, {}) || {};
   const elementDef = Object.values(elements).find((el) => el.tech_name === techName);

   useEffect(() => {
      if (typeof restProps.onFieldsInfoChange === "function") {
         restProps.onFieldsInfoChange(fieldsInfo);
      }
   }, [JSON.stringify(fieldsInfo)]);

   return (
      <td {...restProps}>
         {elementDef ? (
            <GridElementRenderer
               element={elementDef}
               elements={elements}
               form={form}
               initialValue={rawValue}
               tableDisabled={record.tableDisabled}
               gridFormId={formId}
            />
         ) : (
            children
         )}
      </td>
   );
};

const ListTableField = memo(({ element, form, isVisible, formId, rowDataId }) => {
   const intl = useIntl();
   const { getFieldDecorator } = form;
   const { isAnyFieldInFormLoading, isAnyRowEditing, setIsAnyRowEditing, updateCheckboxSelection } =
      useMessageItem();
   const {
      formData,
      changeFieldPendingStatus,
      setFieldsInfo,
      fieldsInfo,
      pendingRequests,
      fieldEnumMap,
   } = useFormContext();
   const dispatch = useDispatch();
   const [rows, setRows] = useState([]);
   const [page, setPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(element.recordsPerPage || 10);
   const hasInitialLoad = useRef(false);
   const [filters, setFilters] = useState([]);
   const {
      gridFormRefs,
      listTableResponses,
      sqlValues = [],
      listTablePromises = {},
   } = useSelector(({ formsv2 }) => formsv2);
   const [isRowEdited, setIsRowEdited] = useState(false);
   const [listData, setListData] = useState(null);
   const [sumValues, setSumValues] = useState({});
   const initialFilters = useRef([]);
   const [addRowModalOpen, setAddRowModalOpen] = useState(false);
   const [selectedRowKeys, setSelectedRowKeys] = useState([]);
   const [rowErrors, setRowErrors] = useState({});
   const [infoText, setInfoText] = useState(null);
   const [isInitialized, setIsInitialized] = useState(false);
   const canAdd = element?.allowAdding === "1";
   const canEdit = element?.allowEditing === "1";
   const canDelete = element?.allowDeleting === "1";
   const hasCheckbox = listData?.columns?.some((col) => col.label === "api_checkbox") ?? false;
   const isFieldVisible = isVisible(element);
   const virtualEditing = element?.allowVirtualEdit === "1";
   const [currentAction, setCurrentAction] = useState(null);

   const allFieldsWithAlerts = getAllFieldsWithAlerts(formData?.forms_data?.tab?.[0]?.elements);

   const parentFieldValues = (element?.listTableFilters || []).map((listFilter) =>
      form.getFieldValue(listFilter?.main_form_field_to_filter_techname),
   );

   const draftsRef = useRef(new Map());
   const getDraftKey = (recordId) => `${element.tech_name}:${recordId}`;

   useEffect(() => {
      let resolvePromise;
      const initializationPromise = new Promise((resolve) => {
         resolvePromise = resolve;
      });
      initializationPromise.resolve = resolvePromise;

      dispatch(setListTablePromise(element.tech_name, initializationPromise));

      return () => {
         dispatch(resolveListTablePromise(element.tech_name));
      };
   }, [element.tech_name]);

   const handlePageChange = (currentPage) => {
      setPage(currentPage);
      getListData();
   };

   const getUniqueGridFormRef = (refs, key) => {
      return refs.find(
         (ref, idx, arr) => ref.id === key && arr.findIndex((r) => r.id === key) === idx,
      )?.ref;
   };

   const snapshotDraftFromRowForm = (recordId) => {
      if (recordId === null || recordId === undefined) return;
      if (!listData?.columns?.length) return;

      const formRefKey = getDraftKey(recordId);
      const gridFormRef = getUniqueGridFormRef(gridFormRefs, formRefKey);
      if (!gridFormRef) return;

      const raw = gridFormRef.getFieldsValue();
      const values = transformFormValuesForRequest(raw, null, undefined, fieldEnumMap);

      const techToLabel = new Map();
      listData.columns.forEach((col) => {
         const techName = col?.options?.techName;
         if (techName) techToLabel.set(techName, col.label);
      });

      const labeled = {};
      Object.keys(values || {}).forEach((techName) => {
         const label = techToLabel.get(techName);
         if (label) labeled[label] = values[techName];
      });

      const prev = draftsRef.current.get(formRefKey) || {};
      draftsRef.current.set(formRefKey, { ...prev, ...labeled });

      setRows((prevRows) =>
         prevRows.map((row) => (row.mainRecordId === recordId ? { ...row, ...labeled } : row)),
      );
   };

   const getRecordFromListData = (recordId) => {
      return listData?.data?.find((record) => record?.api_rowMetaData?.recordId === recordId);
   };

   const updateFormFieldValue = (rowsToUse = rows) => {
      const isFilteringField = (element?.listTableFilters || []).some(
         (filter) => filter.main_form_field_table_list_techname === element.tech_name,
      );

      if (isFilteringField) {
         const selectedRecords = rowsToUse.filter((row) =>
            selectedRowKeys.includes(row.mainRecordId),
         );
         if (typeof form.getFieldProps === "function") {
            const existing = form.getFieldValue(element.tech_name);
            form.getFieldProps(element.tech_name, { initialValue: existing });
         }
         form.setFieldsValue({
            [element.tech_name]: selectedRecords.length > 0 ? selectedRecords : rowsToUse,
         });
      }
   };

   const getHiddenFields = (recordId, columnsForSave) => {
      const record = getRecordFromListData(recordId);
      if (!record) return {};
      const hiddenFields = {};

      columnsForSave.forEach(({ title, techName }) => {
         if (title in record) {
            hiddenFields[techName] = record[title];
         }
      });
      return hiddenFields;
   };

   const handleSave = async (recordId, columnsFromResponse) => {
      if (virtualEditing) {
         const gridFormRef = getUniqueGridFormRef(gridFormRefs, getDraftKey(recordId));
         if (!gridFormRef) {
            Message.error(intl.formatMessage({ id: "forms.errorWhileSaving" }));
            return;
         }
         const formValues = transformFormValuesForRequest(
            gridFormRef.getFieldsValue(),
            null,
            undefined,
            fieldEnumMap,
         );

         const techNameToLabelMap = {};
         columnsFromResponse.forEach((col) => {
            const techName = col?.options?.techName;
            if (techName) techNameToLabelMap[techName] = col.label;
         });

         const labeledValues = {};
         for (const techName in formValues) {
            const label = techNameToLabelMap[techName];
            if (label) labeledValues[label] = formValues[techName];
         }

         draftsRef.current.set(getDraftKey(recordId), {
            ...(draftsRef.current.get(getDraftKey(recordId)) || {}),
            ...labeledValues,
         });

         const modifiedRows = rows.map((row) =>
            row.mainRecordId === recordId
               ? {
                    ...row,
                    ...labeledValues,
                    tableDisabled: false,
                    editable: false,
                 }
               : row,
         );

         updateFormFieldValue(modifiedRows);
         setRows(modifiedRows);
         setIsRowEdited(true);
         setIsAnyRowEditing(false);
         return;
      }

      const gridFormRef = getUniqueGridFormRef(gridFormRefs, getDraftKey(recordId));
      if (!gridFormRef) {
         Message.error(intl.formatMessage({ id: "forms.errorWhileSaving" }));
         return;
      }
      const columnsForSave = columnsFromResponse
         .filter((column) => column.label.includes("[u]"))
         .map((column) => ({
            title: column.label,
            techName: column.options.techName,
         }));

      const hiddenFields = getHiddenFields(recordId, columnsForSave);

      const doSave = () => {
         gridFormRef.validateFields(async (err) => {
            if (err) {
               Message.error(intl.formatMessage({ id: "forms.invalid" }));
               return;
            }
            try {
               const response = await saveFormV2(
                  listData.meta.displayFormChildId,
                  formData?._formKey,
                  transformFormValuesForRequest(
                     {
                        ...gridFormRef.getFieldsValue(),
                        ...hiddenFields,
                     },
                     null,
                     undefined,
                     fieldEnumMap,
                  ),
                  recordId,
               );

               draftsRef.current.delete(getDraftKey(recordId));

               setRows((prevRows) =>
                  prevRows.map((row) =>
                     row.mainRecordId === recordId
                        ? {
                             ...row,
                             tableDisabled: false,
                             editable: false,
                             api_rowMetaData: {
                                recordId: response.data.default?.recordInfo?.[0].id,
                             },
                          }
                        : row,
                  ),
               );
               getListData();
               Message.destroy();
               Message.success(intl.formatMessage({ id: "form.doneProcessing" }));
            } catch (error) {
               Message.destroy();
               Message.error(intl.formatMessage({ id: "form.errorWhileSaving" }));
            }
         });
      };

      if (!allFieldsWithAlerts.length) {
         doSave();
         return;
      }

      handleAllAlerts(
         allFieldsWithAlerts,
         form,
         formData?._formKey,
         (alerts) => {
            if (Object.values(alerts).some((alert) => !!alert)) {
               Message.error(intl.formatMessage({ id: "forms.invalid" }));
               return;
            }
            doSave();
         },
         () => {},
         fieldEnumMap,
      );
   };

   const handleAddRow = () => {
      if (virtualEditing) {
         const newId = uuidv4();
         const newRow = {
            key: newId,
            editable: true,
            mainRecordId: newId,
         };
         setRows([...rows, newRow]);
         setIsRowEdited(true);
         setTimeout(() => {
            const firstInput = document.querySelector(".editable-row input[type=text]");
            if (firstInput && typeof firstInput.scrollIntoView === "function") {
               firstInput.scrollIntoView({ behavior: "smooth", block: "center" });
               firstInput.focus();
            }
         }, 100);
      } else {
         setAddRowModalOpen(true);
      }
   };

   const handleColumnFilter = (columnTechname) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: "1rem" }}>
            <Input
               type="text"
               value={selectedKeys?.[0]}
               onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={handleFilter(columnTechname, selectedKeys?.[0], confirm)}
            />
            <Button
               onClick={handleFilter(columnTechname, selectedKeys?.[0], confirm)}
               buttonType="primary"
            >
               <IntlMessages id="filter.search" />
            </Button>
            <Button
               onClick={handleFilterReset(columnTechname, setSelectedKeys, clearFilters)}
               buttonType="primary"
            >
               <IntlMessages id="filter.reset" />
            </Button>
         </div>
      ),
      filterIcon: (filtered) => (
         <SearchOutlined style={{ color: filtered ? "var(--second)" : "" }} />
      ),
   });

   const handleFilterReset = (columnTechname, setSelectedKeys, clearFilters) => (_e) => {
      setFilters((prevFilters) =>
         prevFilters.filter((filter) => filter.columnTechname !== columnTechname),
      );
      setSelectedKeys([]);
      clearFilters();
      getListData();
   };

   const handleColumnSorter = (columnTitle) => ({
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => {
         const x = a[columnTitle]?.value || a[columnTitle];
         const y = b[columnTitle]?.value || b[columnTitle];

         return x > y ? -1 : 1;
      },
   });

   const handleRemoveRecord = async (recordId) => {
      try {
         const record = getRecordFromListData(recordId);
         const body = {
            key: element?.operationKey,
            records: [
               {
                  ...record,
                  api_rowMetaData: { recordId },
               },
            ],
         };

         const listId = element?.num1;
         await deleteListRows(body, listId, formData?._formKey);

         getListData();
         Message.success(intl.formatMessage({ id: "form.doneProcessing" }));
      } catch (error) {
         Message.error(intl.formatMessage({ id: "form.errorWhileDeleting" }));
      } finally {
      }
   };

   const memoColumns = useMemo(() => {
      const columns = [];
      const shouldAddActionColumn = canAdd && !!listData?.meta?.addItemFormId;
      const shouldShowEditActions = isRowEdited && !shouldAddActionColumn;
      const shouldShowVirtualEdit = canEdit && virtualEditing;
      const buttonStyle = {
         width: 64,
         display: "flex",
         border: "none",
         margin: "auto",
         padding: 0,
      };

      const renderAddButton = () => (
         <Button buttonType="primary" onClick={handleAddRow} style={{ ...buttonStyle, width: 32 }}>
            <Icon_Plus2 fill="var(--btn-primaryText)" />
         </Button>
      );

      const renderRowActions = (row) => {
         const hasSqlFieldInRow = () => {
            if (!listData?.columns) return false;
            return listData.columns.some((col) => col.options?.enumType === "sql");
         };

         if (row.editable) {
            return (
               <Button
                  onClick={() => handleSave(row.mainRecordId, listData.columns)}
                  buttonType="primary"
                  disabled={
                     (hasSqlFieldInRow() ? !Object.keys(sqlValues || {}).length > 0 : false) ||
                     rowErrors[row.mainRecordId]?.length > 0 ||
                     isAnyFieldInFormLoading
                  }
               >
                  <IntlMessages id="global.save" />
               </Button>
            );
         }

         const actions = [];
         if (canEdit || virtualEditing) {
            actions.push({
               key: "edit",
               label: <IntlMessages id="global.edit" />,
               onClick: () => handleRowClick(row),
               disabled: isAnyRowEditing && !row.editable,
            });
         }

         if (canDelete) {
            actions.push({
               key: "delete",
               label: <IntlMessages id="global.delete" />,
               onClick: () => handleRemoveRecord(row.mainRecordId),
            });
         }

         if (listData.actions && listData.actions.length) {
            const handleActionClick = (actionButton) => {
               const { type, formID, id } = actionButton;
               const recordId = row.api_rowMetaData.recordId;

               switch (type) {
                  case "FORM":
                     return setCurrentAction({
                        formId: formID,
                        actionId: id,
                        dataId: recordId,
                     });
                  case "GOOGLE_NAV":
                     return googleNav({ actionId: id, recordId, intl });
                  case "PROCESS":
                     return handleProcessAction(actionButton, recordId);
                  case "OPEN_URL": {
                     const column = listData.columns?.find(
                        (col) => col?.options?.techName === actionButton.urlColumn,
                     );
                     const targetUrl = column ? row[column.label] : null;
                     let parsedUrl;
                     try {
                        parsedUrl = targetUrl ? new URL(targetUrl) : null;
                     } catch {
                        parsedUrl = null;
                     }
                     if (parsedUrl && ["https:", "http:"].includes(parsedUrl.protocol)) {
                        window.open(targetUrl, "_blank", "noopener,noreferrer");
                     } else {
                        Message.error(intl.formatMessage({ id: "actions.openUrlError" }));
                     }
                     return null;
                  }
                  default:
                     return null;
               }
            };

            const availableActionKeys = new Set(row.api_recordActions?.map((a) => a.id) || []);

            const matchedActions = listData.actions.filter((action) =>
               availableActionKeys.has(action.id),
            );

            matchedActions.forEach((actionButton) => {
               actions.push({
                  key: actionButton.key,
                  label: actionButton.label,
                  onClick: () => handleActionClick(actionButton),
                  disabled: actionButton.disabled,
               });
            });
         }

         if (actions.length === 1) {
            const action = actions[0];
            return (
               <Button
                  buttonType="primary"
                  onClick={action.onClick}
                  disabled={action.disabled || pendingRequests.current > 0}
               >
                  {action.label}
               </Button>
            );
         }

         if (actions.length > 1) {
            return (
               <RowActionsDropdownTrigger
                  actions={actions}
                  buttonLabel={<IntlMessages id="global.actions" />}
               />
            );
         }

         return null;
      };

      if (
         shouldAddActionColumn ||
         shouldShowEditActions ||
         shouldShowVirtualEdit ||
         !!listData?.actions?.length
      ) {
         columns.push({
            title: shouldAddActionColumn ? renderAddButton() : <IntlMessages id="global.actions" />,
            dataIndex: "actions",
            className: "actions-column",
            key: "actions",
            render: (_text, row) => {
               if (virtualEditing && !row.editable && !("api_rowMetaData" in row)) return null;
               return renderRowActions(row);
            },
         });
      }

      if (hasCheckbox) {
         const allSelected =
            rows.length > 0 && rows.every((row) => selectedRowKeys.includes(row.mainRecordId));
         const someSelected = rows.some((row) => selectedRowKeys.includes(row.mainRecordId));

         const handleSelectAll = (e) => {
            if (e.target.checked) setSelectedRowKeys(rows.map((row) => row.mainRecordId));
            else setSelectedRowKeys([]);
         };

         const handleSelectOne = (recordId, checked) => {
            setSelectedRowKeys((keys) =>
               checked ? [...keys, recordId] : keys.filter((k) => k !== recordId),
            );
         };

         columns.push({
            title: (
               <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={handleSelectAll}
               />
            ),
            dataIndex: "api_checkbox",
            className: "checkbox-column",
            key: "api_checkbox",
            align: "center",
            render: (_text, row) => (
               <Checkbox
                  checked={selectedRowKeys.includes(row.mainRecordId)}
                  onChange={(e) => handleSelectOne(row.mainRecordId, e.target.checked)}
                  disabled={pendingRequests.current > 0}
               />
            ),
         });
      }

      listData?.columns?.forEach((column) => {
         const isHidden = column?.options?.hidden === "1";
         const techName = column?.options?.techName;
         if (!techName || column.label === "api_checkbox") return;

         columns.push({
            title: column.label,
            dataIndex: column.label,
            key: techName,
            techName,
            enumType: column.options.enumType,
            precision: column.options.precision,
            sumColumn: column.options.sumColumn,
            sumAllRecords: column.options.sumAllRecords,

            onHeaderCell: () => ({
               style: { display: isHidden ? "none" : undefined },
            }),

            onCell: (record, rowIndex) => ({
               record,
               rowIndex,
               dataIndex: column.label,
               techName,
               style: { display: isHidden ? "none" : undefined },
               onFieldsInfoChange: (fieldsInfo) => {
                  setRowErrors((prev) => ({
                     ...prev,
                     [record.mainRecordId]:
                        fieldsInfo && Object.keys(fieldsInfo).length > 0
                           ? Object.values(fieldsInfo)[0]
                           : null,
                  }));
               },
            }),

            ...(!isHidden ? handleColumnFilter(techName) : {}),
            ...(!isHidden ? handleColumnSorter(column.label) : {}),
         });
      });

      return columns;
   }, [
      page,
      filters,
      gridFormRefs,
      listData?.data?.length,
      rows,
      isRowEdited,
      selectedRowKeys.length,
      sqlValues,
      isAnyRowEditing,
      JSON.stringify(rowErrors),
      isAnyFieldInFormLoading,
      pendingRequests.current > 0,
   ]);

   const handleFilter = (columnTechname, value, confirm) => (_e) => {
      setFilters((prevFilters) => {
         if (!value)
            return prevFilters.filter((filter) => filter.columnTechname !== columnTechname);
         if (!prevFilters.find((filter) => filter.columnTechname === columnTechname)) {
            return [...prevFilters, { columnTechname, value }];
         }
         return prevFilters.map((filter) =>
            filter.columnTechname === columnTechname && filter.value !== value
               ? { columnTechname, value }
               : filter,
         );
      });
      confirm();
      getListData();
   };

   const getListData = () => {
      if (!element.num1) {
         Message.error(intl.formatMessage({ id: "forms.noListIdInListTable" }));
         return;
      } else if (
         element?.listTableFilters?.length > 0 &&
         initialFilters.current?.length === 0 &&
         rows?.length === 0
      ) {
         return;
      } else if (initialFilters.current?.length > 0 || filters?.length > 0) {
         getListV2(element.num1, page, itemsPerPage, [...initialFilters.current, ...filters])
            .then((res) => {
               try {
                  handleListResponse(res);
               } catch (error) {
                  Message.error(intl.formatMessage({ id: "forms.errorWhileFetchingData" }));
               } finally {
                  setIsRowEdited(false);
               }
            })
            .catch(() => Message.error(intl.formatMessage({ id: "forms.errorWhileFetchingData" })));
      } else {
         getList(element.num1, page, itemsPerPage)
            .then((res) => {
               try {
                  handleListResponse(res);
               } catch (error) {
                  Message.error(intl.formatMessage({ id: "forms.errorWhileFetchingData" }));
               } finally {
                  setIsRowEdited(false);
               }
            })
            .catch(() =>
               Message.error(
                  intl.formatMessage({ id: "forms.errorWhileFetchingDataForListTable" }),
               ),
            );
      }
   };

   const mergeRows = (currentRows, backendRows) => {
      const currentMap = new Map(
         currentRows.map((r) => [r?.api_rowMetaData?.recordId, r]).filter(([id]) => id),
      );
      return backendRows.map((row) => {
         const update = currentMap.get(row?.api_rowMetaData?.recordId);
         return update ? { ...row, ...update } : row;
      });
   };

   const processRowData = (rawRows) => {
      return rawRows.map((row) => ({
         ...Object.fromEntries(
            Object.entries(row).map(([key, val]) => [
               key,
               val && typeof val === "object" && "id" in val && "value" in val
                  ? String(val.value)
                  : val,
            ]),
         ),
         mainRecordId: row.api_rowMetaData?.recordId,
         tableDisabled: false,
         editable: false,
      }));
   };

   const applyDraftsToRows = (backendRows) => {
      return (backendRows || []).map((row) => {
         const draft = draftsRef.current.get(getDraftKey(row.mainRecordId));
         return draft ? { ...row, ...draft } : row;
      });
   };

   const handleListResponse = (res) => {
      const responseData = isActivePublicUser() ? res?.data?.getPublicList : res?.data?.default;
      setListData(responseData);

      const processedRows = processRowData(responseData.data);
      const processedWithDrafts = virtualEditing ? applyDraftsToRows(processedRows) : processedRows;

      const rowsToSet = mergeRows(rows, processedWithDrafts);
      setRows(rowsToSet);

      const hasCheckbox = responseData?.columns?.some((col) => col.label === "api_checkbox");
      if (hasCheckbox) {
         const selectedIds = rowsToSet
            .filter((row) => row.api_checkbox === "1")
            .map((row) => row.mainRecordId);
         setSelectedRowKeys(selectedIds);
      }

      updateFormFieldValue(rowsToSet);

      setIsRowEdited(false);
      setIsInitialized(true);

      dispatch(resolveListTablePromise(element.tech_name));
   };

   const handleRowClick = (record, event) => {
      if (!canEdit) return;

      setRows((prevRows) =>
         prevRows.map((row) =>
            row.mainRecordId === record.mainRecordId
               ? { ...row, editable: true, tableDisabled: false }
               : row,
         ),
      );
      setIsRowEdited(true);
      setIsAnyRowEditing(true);
   };

   useEffect(() => {
      if (listTableResponses[element.tech_name]) {
         const response = listTableResponses[element.tech_name];

         if (response?.data) {
            handleListResponse({
               data: {
                  default: response,
                  getPublicList: response,
               },
            });
            delete listTableResponses[element.tech_name];
         } else {
            Message.error(intl.formatMessage({ id: "forms.errorWhileFetchingData" }));
         }
      }
   }, [listTableResponses]);

   useEffect(() => {
      const getSumValues = async () => {
         try {
            let frontendSums = {};
            let backendSums = {};
            const columnsToSum = memoColumns?.filter((column) => column?.sumColumn === "1");

            const columnTitles = columnsToSum
               .filter((column) => column?.sumColumn === "1")
               .map((column) => column?.title);

            const columnTechnames = columnsToSum
               .filter((column) => column?.sumAllRecords === "1")
               .map((column) => column?.techName);

            if (columnTitles.length) {
               frontendSums = columnTitles.reduce((acc, title) => {
                  const columnCells = document.querySelectorAll(`td[title="${title}"]`);
                  const sum = Array.from(columnCells).reduce((acc, element) => {
                     const cellValue = !isNaN(Number(element.innerHTML))
                        ? Number(element.innerHTML)
                        : Number(element.querySelector("input")?.value);

                     return acc + cellValue;
                  }, 0);

                  return { ...acc, [title]: sum.toFixed(2) };
               }, {});
            }

            if (columnTechnames.length) {
               const res = await getColumnSum(element?.num1, columnTechnames, [
                  ...initialFilters.current,
                  ...filters,
               ]);
               backendSums = Object.entries(res?.data?.getSumColumns).reduce(
                  (acc, [techname, value]) => {
                     const columnData = memoColumns?.find(
                        (column) => column?.techName === techname,
                     );
                     acc[intl.formatMessage({ id: "global.sumAllPages" }) + columnData?.title] =
                        value;
                     return acc;
                  },
                  {},
               );
            }

            setSumValues({ ...frontendSums, ...backendSums });
         } catch {
            Message.error(intl.formatMessage({ id: "listTable.sumError" }));
            setSumValues({});
         }
      };
      getSumValues();
   }, [isRowEdited, memoColumns]);

   useEffect(() => {
      const initialTableFilters = (element?.listTableFilters || []).map((listFilter) => {
         const fieldValue = form.getFieldValue(listFilter.main_form_field_to_filter_techname);
         let value = fieldValue;
         if (fieldValue && typeof fieldValue === "object") {
            value = fieldValue.value ?? fieldValue.id ?? "";
         }
         return {
            columnTechname: listFilter.list_form_field_to_filter_techname,
            value,
            isStrict: listFilter.is_strict,
         };
      });
      initialFilters.current = initialTableFilters;
   }, [element?.listTableFilters, ...parentFieldValues]);

   useEffect(() => {
      if (!form || !element || !isInitialized) return;

      updateFormFieldValue();

      const isFilteringField = (element?.listTableFilters || []).some(
         (filter) => filter.main_form_field_table_list_techname === element.tech_name,
      );

      if (isFilteringField) {
         const runRelatedFieldProcessing = async () => {
            const allPromises = Object.values(listTablePromises);
            if (allPromises.length > 0) {
               await Promise.all(allPromises);
            }

            handleChangedRelatedField(
               { ...element, isRelatedWithOtherFields: true },
               form,
               setFieldsInfo,
               allFieldsWithAlerts,
               formId,
               intl,
               formData?._formKey,
               rowDataId,
               changeFieldPendingStatus,
               fieldEnumMap,
            );
         };

         runRelatedFieldProcessing();
      }
   }, [isInitialized, selectedRowKeys.length, Object.keys(listTablePromises).length, fieldEnumMap]);

   const isCheckboxRequired = element?.obow === "1";

   useEffect(() => {
      updateCheckboxSelection(
         element.tech_name,
         !isFieldVisible ||
            !hasCheckbox ||
            !isCheckboxRequired ||
            rows.length === 0 ||
            selectedRowKeys.length > 0,
      );
      return () => updateCheckboxSelection(element.tech_name, true);
   }, [
      isFieldVisible,
      hasCheckbox,
      isCheckboxRequired,
      rows.length,
      selectedRowKeys.length,
      element.tech_name,
      updateCheckboxSelection,
   ]);

   useEffect(() => {
      if (!hasInitialLoad.current) {
         hasInitialLoad.current = true;
         getListData();
      }
   }, [element.num1]);

   useEffect(() => {
      const infoText = fieldsInfo[element.tech_name];
      setInfoText(infoText);
      handleTemplateAlert(element.tech_name, infoText);
   }, [fieldsInfo[element.tech_name]]);

   const addRowDefaultValues = useMemo(() => {
      if (!addRowModalOpen || !element?.listTableFilters?.length) return undefined;
      const allValues = form.getFieldsValue();
      return element.listTableFilters.reduce((acc, filter) => {
         const parentTechname = filter.main_form_field_to_filter_techname;
         const childTechname = filter.list_form_field_to_filter_techname;
         if (parentTechname && childTechname && allValues[parentTechname] !== undefined) {
            acc[childTechname] = allValues[parentTechname];
         }
         return acc;
      }, {});
   }, [addRowModalOpen]);

   const getAddRowAdditionalSaveData = useCallback(
      () => ({
         _parent: {
            ...transformFormValuesForRequest(form.getFieldsValue(), null, undefined, fieldEnumMap),
            UIF_ID: formId,
            easyForm: true,
         },
      }),
      [form, formId, fieldEnumMap],
   );

   return (
      <Form.Item
         key={element.tech_name}
         className={`${element?.type}-field field_${element.tech_name} list-widget`}
         style={{
            display: isVisible(element) ? "" : "none",
            gridArea: handleGridTechname(element.tech_name),
         }}
      >
         {getFieldDecorator(element.tech_name)(
            !isInitialized ? (
               <div className="spin-middle">
                  <Spin />
               </div>
            ) : (
               <>
                  <Table
                     rowKey="mainRecordId"
                     components={{
                        body: {
                           row: EditableFormRow,
                           cell: EditableCell,
                        },
                     }}
                     rowClassName={`editable-row`}
                     className={`add-record-btn-${!!listData?.meta?.addItemFormId}`}
                     dataSource={roundNumbersInListTable(rows, memoColumns?.precision)}
                     columns={memoColumns}
                     scroll={{ x: true }}
                     pagination={false}
                     bordered
                     size="middle"
                     loading={!isInitialized}
                     onRow={(record, index) => ({
                        index,
                        record,
                        mainRecordId: record.mainRecordId,
                        formRefKey: `${element.tech_name}:${record.mainRecordId}`,
                        onRowBlurSnapshot: () => snapshotDraftFromRowForm(record.mainRecordId),
                        displayFormChildId:
                           listData?.meta?.displayFormChildId &&
                           listData?.meta?.displayFormChildId !== "0"
                              ? listData.meta.displayFormChildId
                              : null,
                     })}
                  />
                  <FieldInfo text={infoText} />
                  {isCheckboxRequired &&
                     hasCheckbox &&
                     rows.length > 0 &&
                     selectedRowKeys.length === 0 && (
                        <FieldInfo
                           text={intl.formatMessage({
                              id: "listTable.selectAtLeastOne",
                              defaultMessage: "Zaznacz co najmniej jeden wiersz",
                           })}
                        />
                     )}
                  <ListSum sumValues={sumValues} />
                  {listData?.meta?.totalPages > 1 && (
                     <div className="listTable-field__pagination">
                        <Pagination
                           initialPage={1}
                           totalItems={listData.meta.totalItemsCount}
                           defaultPageSize={itemsPerPage}
                           onChange={handlePageChange}
                        />
                     </div>
                  )}
                  {renderDevInfo(element)}
                  <Modal
                     isOpen={currentAction?.dataId ?? addRowModalOpen}
                     onCancel={() =>
                        currentAction?.dataId ? setCurrentAction(null) : setAddRowModalOpen(false)
                     }
                     topBarStyles={{ border: "none", padding: "10px" }}
                     width={1000}
                     height={700}
                     mainStyles={{ borderRadius: "15px", resize: "none" }}
                     footerContent={
                        <Button
                           className="dedicated-list-table__cancel-btn"
                           buttonType="third"
                           onClick={() => {
                              currentAction?.dataId
                                 ? setCurrentAction(null)
                                 : setAddRowModalOpen(false);
                           }}
                        >
                           <IntlMessages id="Cancel" />
                        </Button>
                     }
                  >
                     {currentAction ? (
                        <FormComplete
                           actionId={currentAction?.actionId}
                           recordId={currentAction?.dataId}
                           saveCallback={() => {
                              setCurrentAction(null);
                              getListData();
                           }}
                        />
                     ) : (
                        <FormComplete
                           formId={listData?.meta?.addItemFormId}
                           defaultValues={addRowDefaultValues}
                           additionalSaveData={getAddRowAdditionalSaveData}
                           saveCallback={() => {
                              setAddRowModalOpen(false);

                              Message.destroy();
                              Message.success(
                                 intl.formatMessage({
                                    id: "addRecordBtn.newOptionHasBeenAdded",
                                 }),
                                 5000,
                              );
                              setTimeout(() => {
                                 getListData();
                              }, 1000);
                           }}
                        />
                     )}
                  </Modal>
               </>
            ),
         )}
      </Form.Item>
   );
});

ListTableField.propTypes = {
   element: PropTypes.object.isRequired,
   form: PropTypes.any,
   isVisible: PropTypes.func,
};

export default ListTableField;
